import { Firmware, FirmwareQueryParam, FirmwareStatusEnum, FirmwareUploadParam } from '/@/types/device-firmware'
import request, { CommonListResponse, IListWorkspaceResponse, IPage, IWorkspaceResponse } from '/@/api/http/request'
import { Device } from '/@/types/device'
import { DeviceDictionary, DeviceDictionaryBody, DeviceDictionaryQueryParam } from '/@/types/device-dictionary'
import { WorkspaceSystemSettings } from '/@/types/system-settings'

const HTTP_PREFIX = '/manage/api/v1'

// login
export interface LoginBody {
 username: string,
 password: string,
 flag: number,
}
export interface BindBody {
  device_sn: string,
  user_id: string,
  workspace_id: string,
  domain?: string
}
export interface CreateUserBody {
  username: string,
  password: string,
  user_type: number,
  enabled?: boolean,
  mqtt_username: string,
  mqtt_password: string,
}
export interface HmsQueryBody {
  sns: string[],
  children_sn: string,
  device_sn: string,
  language: string,
  level: number | string,
  begin_time: number,
  end_time: number,
  message: string,
  domain: number,
}

export const login = async function (body: LoginBody): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/login`
  const result = await request.post(url, body)
  return result.data
}

// Refresh Token
export const refreshToken = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/token/refresh`
  const result = await request.post(url, body)
  return result.data
}

// Get Platform Info
export const getPlatformInfo = async function (): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/current`
  const result = await request.get(url)
  return result.data
}

export const getWorkspaceSystemSettings = async function (): Promise<IWorkspaceResponse<WorkspaceSystemSettings>> {
  const url = `${HTTP_PREFIX}/workspaces/current/settings`
  const result = await request.get(url)
  return result.data
}

export const updateWorkspaceSystemSettings = async function (body: WorkspaceSystemSettings): Promise<IWorkspaceResponse<WorkspaceSystemSettings>> {
  const url = `${HTTP_PREFIX}/workspaces/current/settings`
  const result = await request.put(url, body)
  return result.data
}

// Get User Info
export const getUserInfo = async function (): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/current`
  const result = await request.get(url)
  return result.data
}

// Get Device Topo
export const getDeviceTopo = async function (workspace_id: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices`
  const result = await request.get(url)
  return result.data
}

// Get Livestream Capacity
export const getLiveCapacity = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/capacity`
  const result = await request.get(url, body)
  return result.data
}

// Start Livestream
export const startLivestream = async function (body: {}, options?: { skipErrorMessage?: boolean }): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/start`
  const result = await request.post(url, body, options?.skipErrorMessage ? { skipErrorMessage: true } as any : undefined)
  return result.data
}

// Stop Livestream
export const stopLivestream = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/stop`
  const result = await request.post(url, body)
  return result.data
}
// Refresh Agora audience token
export const refreshAgoraToken = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/agora/token`
  const result = await request.post(url, body)
  return result.data
}

// Start an Agora Media Push Converter on the server.
export const startAliyunLiveRelay = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/aliyun/relays/start`
  const result = await request.post(url, body, { sensitiveResponse: true, skipErrorMessage: true } as any)
  return result.data
}

// Get the server-side Converter state.
export const getAliyunLiveRelayStatus = async function (videoId: string, clientSessionId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/aliyun/relays/status?video_id=${encodeURIComponent(videoId)}&client_session_id=${encodeURIComponent(clientSessionId)}`
  const result = await request.get(url, { sensitiveResponse: true, skipErrorMessage: true } as any)
  return result.data
}

// Stop the server-side Converter manually or when its owner closes the source.
export const stopAliyunLiveRelay = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/aliyun/relays/stop`
  const result = await request.post(url, body, { sensitiveResponse: true, skipErrorMessage: true } as any)
  return result.data
}

// Update Quality
export const setLivestreamQuality = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/update`
  const result = await request.post(url, body)
  return result.data
}

export const getAllUsersInfo = async function (wid: string, body: IPage): Promise<CommonListResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users?&page=${body.page}&page_size=${body.page_size}`
  const result = await request.get(url)
  return result.data
}

export const createUserInfo = async function (wid: string, body: CreateUserBody): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users`
  const result = await request.post(url, body)
  return result.data
}

export const updateUserInfo = async function (wid: string, user_id: string, body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users/${user_id}`
  const result = await request.put(url, body)
  return result.data
}

export const resetUserMqttInfo = async function (wid: string, user_id: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users/${user_id}/mqtt-reset`
  const result = await request.put(url)
  return result.data
}

export const bindDevice = async function (body: BindBody): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${body.device_sn}/binding`
  const result = await request.post(url, body)
  return result.data
}

export const unbindDevice = async function (device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${device_sn}/unbinding`
  const result = await request.delete(url)
  return result.data
}

export const getDeviceBySn = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/${device_sn}`
  const result = await request.get(url)
  return result.data
}

/**
 * 获取绑定设备信息
 * @param workspace_id
 * @param body
 * @param domain
 * @returns
 */
export const getBindingDevices = async function (workspace_id: string, body: IPage, domain: number): Promise<IListWorkspaceResponse<Device>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/bound?&page=${body.page}&page_size=${body.page_size}&domain=${domain}`
  const result = await request.get(url)
  return result.data
}

export const updateDevice = async function (body: {}, workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/${device_sn}`
  const result = await request.put(url, body)
  return result.data
}

export const getUnreadDeviceHms = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms/${device_sn}`
  const result = await request.get(url)
  return result.data
}

export const updateDeviceHms = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms/${device_sn}`
  const result = await request.put(url)
  return result.data
}

export const getDeviceHms = async function (body: HmsQueryBody, workspace_id: string, pagination: IPage): Promise<IListWorkspaceResponse<any>> {
  let url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms?page=${pagination.page}&page_size=${pagination.page_size}` +
    `&level=${body.level ?? ''}&begin_time=${body.begin_time ?? ''}&end_time=${body.end_time ?? ''}&message=${body.message ?? ''}&language=${body.language}`
  body.sns.forEach((sn: string) => {
    if (sn !== '') {
      url = url.concat(`&device_sn=${sn}`)
    }
  })
  const result = await request.get(url)
  return result.data
}

export const changeLivestreamLens = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/switch`
  const result = await request.post(url, body)
  return result.data
}

export const getFirmwares = async function (workspace_id: string, page: IPage, body: FirmwareQueryParam): Promise<IListWorkspaceResponse<Firmware>> {
  const params = new URLSearchParams({
    page: String(page.page),
    page_size: String(page.page_size)
  })
  if (body.device_name) {
    params.set('device_name', body.device_name)
  }
  if (body.product_version) {
    params.set('product_version', body.product_version)
  }
  const status = normalizeFirmwareStatus(body.firmware_status)
  if (status !== undefined) {
    params.set('status', String(status))
  }
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/firmwares?${params.toString()}`
  const result = await request.get(url)
  return result.data
}

function normalizeFirmwareStatus (status: FirmwareQueryParam['firmware_status']): boolean | undefined {
  if (status === FirmwareStatusEnum.TRUE) {
    return true
  }
  if (status === FirmwareStatusEnum.FALSE) {
    return false
  }
  return undefined
}

export const importFirmareFile = async function (workspaceId: string, param: FormData): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/firmwares/file/upload`
  const result = await request.post(url, param)
  return result.data
}

export const changeFirmareStatus = async function (workspaceId: string, firmwareId: string, param: {status: boolean}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/firmwares/${firmwareId}`
  const result = await request.put(url, param)
  return result.data
}

export const getDeviceDictionaries = async function (page: IPage, body: DeviceDictionaryQueryParam): Promise<IListWorkspaceResponse<DeviceDictionary>> {
  const params = new URLSearchParams({
    page: String(page.page),
    page_size: String(page.page_size)
  })
  if (body.domain !== undefined && body.domain !== '') {
    params.set('domain', String(body.domain))
  }
  if (body.device_type !== undefined && body.device_type !== '') {
    params.set('device_type', String(body.device_type))
  }
  if (body.sub_type !== undefined && body.sub_type !== '') {
    params.set('sub_type', String(body.sub_type))
  }
  if (body.device_name) {
    params.set('device_name', body.device_name)
  }
  const url = `${HTTP_PREFIX}/device-dictionaries?${params.toString()}`
  const result = await request.get(url)
  return result.data
}

export const createDeviceDictionary = async function (body: DeviceDictionaryBody): Promise<IWorkspaceResponse<DeviceDictionary>> {
  const url = `${HTTP_PREFIX}/device-dictionaries`
  const result = await request.post(url, body)
  return result.data
}

export const updateDeviceDictionary = async function (id: number, body: DeviceDictionaryBody): Promise<IWorkspaceResponse<DeviceDictionary>> {
  const url = `${HTTP_PREFIX}/device-dictionaries/${id}`
  const result = await request.put(url, body)
  return result.data
}

export const deleteDeviceDictionary = async function (id: number): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/device-dictionaries/${id}`
  const result = await request.delete(url)
  return result.data
}
